// src/pages/ImageEnhancer.jsx
import { useState, useEffect } from "react";
import { Upload, Download, Loader, XCircle, Eye, Trash2, Grid, List } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { getStorage, ref, listAll, getDownloadURL, deleteObject } from "firebase/storage";
import { auth } from "../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { canUseStandardFeature } from "../utils/featureAccess";
// import { toast } from "react-hot-toast";
// import { XCircle } from "lucide-react";
import { toast } from "react-toastify";
const ImageEnhancer = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFilePreview, setSelectedFilePreview] = useState(null);
  const [assets, setAssets] = useState([]);
  const [progress, setProgress] = useState(0);
  const [enhancedImages, setEnhancedImages] = useState([]);
  const [enhancedImageRefs, setEnhancedImageRefs] = useState([]);
  const [userId, setUserId] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [assetViewMode, setAssetViewMode] = useState("grid");
  const [enhancedViewMode, setEnhancedViewMode] = useState("grid");
  const [assetPage, setAssetPage] = useState(1);
  const [enhancedPage, setEnhancedPage] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  const generatedImagesPerPage = 6;
  const assetsImagesPerPage = 8;
  const storage = getStorage();

  // Calculate pagination
  const enhancedPageCount = Math.ceil(enhancedImages.length / generatedImagesPerPage);
  const assetPageCount = Math.ceil(assets.length / assetsImagesPerPage);

  // Get current page items
  const paginatedEnhanced = enhancedImages.slice(
    (enhancedPage - 1) * generatedImagesPerPage,
    enhancedPage * generatedImagesPerPage
  );

  const paginatedAssets = assets.slice(
    (assetPage - 1) * assetsImagesPerPage,
    assetPage * assetsImagesPerPage
  );

  // Add pagination controls after the enhanced images grid
  const EnhancedPagination = () => (
    enhancedPageCount > 1 && (
      <div className="flex flex-wrap justify-center mt-4 gap-1 sm:gap-2">
        <button
          onClick={() => setEnhancedPage((p) => Math.max(1, p - 1))}
          disabled={enhancedPage === 1}
          className="px-2 sm:px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors text-sm"
        >
          Previous
        </button>
        {Array.from({ length: enhancedPageCount }, (_, i) => i + 1).map((number) => (
          <button
            key={number}
            onClick={() => setEnhancedPage(number)}
            className={`px-2 sm:px-3 py-1 rounded-md text-sm ${
              enhancedPage === number
                ? "bg-[#3913C5] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {number}
          </button>
        ))}
        <button
          onClick={() => setEnhancedPage((p) => Math.min(enhancedPageCount, p + 1))}
          disabled={enhancedPage === enhancedPageCount}
          className="px-2 sm:px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors text-sm"
        >
          Next
        </button>
      </div>
    )
  );

  // Add pagination controls after the assets grid
  const AssetsPagination = () => (
    assetPageCount > 1 && (
      <div className="flex flex-wrap justify-center mt-4 gap-1 sm:gap-2">
        <button
          onClick={() => setAssetPage((p) => Math.max(1, p - 1))}
          disabled={assetPage === 1}
          className="px-2 sm:px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors text-sm"
        >
          Previous
        </button>
        {Array.from({ length: assetPageCount }, (_, i) => i + 1).map((number) => (
          <button
            key={number}
            onClick={() => setAssetPage(number)}
            className={`px-2 sm:px-3 py-1 rounded-md text-sm ${
              assetPage === number
                ? "bg-[#3913C5] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {number}
          </button>
        ))}
        <button
          onClick={() => setAssetPage((p) => Math.min(assetPageCount, p + 1))}
          disabled={assetPage === assetPageCount}
          className="px-2 sm:px-3 py-1 bg-[#3913C5] text-white rounded-md disabled:opacity-50 hover:bg-[#2a0d8f] transition-colors text-sm"
        >
          Next
        </button>
      </div>
    )
  );

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userInfo = docSnap.data();
          setUserId(user.uid);
          setUserData(userInfo);
  
          if (!canUseStandardFeature(userInfo)) {
            toast.error("Image Enhancer is only available for subscribed users.");
            navigate("/pricing");
          }
        }
      } else {
        setUserId(null);
        toast.error("Please log in to access this feature.");
        navigate("/");
      }
    });
  
    return () => unsubscribe();
  }, []);
  

  useEffect(() => {
    if (userId) {
      Promise.all([
        fetchImages(`assets/${userId}/models/`),
        fetchImages(`assets/${userId}/garments/`)
      ]).then(([modelUrls, garmentUrls]) => {
        setAssets([...modelUrls, ...garmentUrls]);
      });

      fetchEnhancedImages(`assets/${userId}/enhanced/`);
    }
  }, [userId]);

  const fetchImages = async (folder) => {
    try {
      const assetsRef = ref(storage, folder);
      const res = await listAll(assetsRef);
      const urls = await Promise.all(res.items.map((item) => getDownloadURL(item)));
      return urls;
    } catch (error) {
      console.error("Error fetching images:", error);
      return [];
    }
  };

  const fetchEnhancedImages = async (folder) => {
    try {
      const assetsRef = ref(storage, folder);
      const res = await listAll(assetsRef);
      const urls = await Promise.all(res.items.map((item) => getDownloadURL(item)));
      setEnhancedImages(urls);
      setEnhancedImageRefs(res.items.map((item) => item.fullPath));
    } catch (error) {
      console.error("Error fetching enhanced images:", error);
    }
  };

  const handleDownload = async (url, index) => {
    try {
      const response = await fetch("https://api.styleuai.com/download/download-image", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image_url: url,
          filename: `StyleUAI_enhanced_${index}.jpg`,
        }),
      });
  
      if (!response.ok) throw new Error("Failed to download image");
  
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
  
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `StyleUAI_enhanced_${index}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error:", err);
    }
  };

  const handleDelete = async (index) => {
    if (!enhancedImageRefs[index]) return;

    // Set the image to delete and show dialog
    setImageToDelete(index);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (imageToDelete === null) return;

    try {
      const imageRef = ref(storage, enhancedImageRefs[imageToDelete]);
      await deleteObject(imageRef);

      // Calculate the actual index in the full array
      const actualIndex = (enhancedPage - 1) * generatedImagesPerPage + imageToDelete;
      
      setEnhancedImages((prev) => prev.filter((_, i) => i !== actualIndex));
      setEnhancedImageRefs((prev) => prev.filter((_, i) => i !== actualIndex));

      // Adjust page if necessary
      if (paginatedEnhanced.length === 1 && enhancedPage > 1) {
        setEnhancedPage(prev => prev - 1);
      }

      // Show success message
      toast.success("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
      setError("Failed to delete image.");
      toast.error("Failed to delete image");
    } finally {
      setShowDeleteDialog(false);
      setImageToDelete(null);
    }
  };

 const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (!file) return toast.error("Please select an image!");

  // 1. Check file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    toast.error("Image is too large (max 5MB). Please select a smaller file.");
    return;
  }

  // 2. Read dimensions
  const reader = new FileReader();
  reader.onload = function (e) {
    const img = new Image();
    img.onload = function () {
      if (img.width > 2000 || img.height > 2000) {
        toast.error("Image dimensions exceed 2000x2000px. Please resize before uploading.");
        return;
      }

      // All good → update preview + state
      setSelectedFile(file);
      setSelectedFilePreview(URL.createObjectURL(file));
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
};


  const selectAsset = (url) => {
    setSelectedFile(url);
    setSelectedFilePreview(url);
  };

const enhanceImage = async () => {
  // ✅ Check user credits before generating
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    toast.error("User not found.");
    return;
  }

  const currentCredits = userSnap.data().credits || 0;
  const requiredCredits = 1; // Image enhancement costs 1 credit

  if (currentCredits < requiredCredits) {
    toast.error("Insufficient credits. Buy more.");
    return;
  }

  setProcessing(true);
  setProgress(0);
  setError(null);

  let progressInterval = setInterval(() => {
    setProgress((prev) => (prev < 90 ? prev + 10 : prev));
  }, 500);

  try {
    let response;
    if (typeof selectedFile === "string") {
      response = await fetch("https://api.styleuai.com/image-enhancer/enhance-image", {
      // response = await fetch("http://localhost:8055/enhance-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_url: selectedFile, user_id: userId }),
      });
    } else {
      const formData = new FormData();
      formData.append("image", selectedFile, "selected-image.png");
      formData.append("user_id", userId);

      response = await fetch("https://api.styleuai.com/image-enhancer/enhance-image", {
      // response = await fetch("http://localhost:8055/enhance-image", {
        method: "POST",
        body: formData,
      });
    }

    if (!response.ok) throw new Error(await response.text());

    await fetchEnhancedImages(`assets/${userId}/enhanced/`);

    // ✅ Deduct credit after successful enhancement
    await updateDoc(userRef, { credits: currentCredits - requiredCredits });
    
    // Clear preview after successful enhancement (like modern websites)
    setSelectedFile(null);
    setSelectedFilePreview(null);
    
    toast.success("Image enhanced successfully!");
  } catch (error) {
    console.error(error);
    toast.error(`Enhancement failed: ${error.message}`);
    setError(error.message);
  } finally {
    clearInterval(progressInterval);
    setProgress(100);
    setTimeout(() => {
      setProcessing(false);
      setProgress(0);
    }, 500);
  }
};

  
  return (
    <div className="flex h-screen">
      <div className="hidden md:block">
    <Sidebar />
  </div>
      <div className="flex flex-col flex-1 bg-white overflow-x-hidden">
       <div className="absolute top-2 md:top-4 right-2 md:right-4">
    <Header />
  </div>

<div className="block md:hidden">
    <Sidebar />
  </div>
  <div className="content-container">
        <div className="p-4 sm:p-6 flex flex-col gap-4 sm:gap-6">
          {/* Top Section - Upload and Enhanced Images */}
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
            {/* Left Section - Upload */}
          <div className="w-full lg:w-1/2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Image Enhancer</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Enhance your images with AI-based quality and resolution boost.</p>
              </div>
            </div>

            {/* Image Upload with Preview */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="relative w-full h-64 border-2 border-dashed border-gray-300 rounded-lg bg-[#F5F2FF] flex items-center justify-center hover:border-[#3913C5] transition-colors">
              {selectedFilePreview ? (
                <div className="relative w-full h-full">
                  <img src={selectedFilePreview} alt="Preview" className="w-full h-full object-contain p-2" />
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFile(null);
                      setSelectedFilePreview(null);
                    }}
                    className="absolute top-2 right-2 bg-white border border-gray-300 text-gray-700 rounded-full p-1 hover:bg-gray-100 shadow"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload
                    className="w-12 h-12 text-[#3913C5] mx-auto cursor-pointer"
                    onClick={() => document.getElementById("hiddenImageInput").click()}
                  />
                  <p className="text-gray-600 mt-2">Click the arrow to select an image</p>
                  <p className="text-gray-400 text-sm">JPG, PNG files less than 10MB</p>
                </div>
              )}
              {/* Hidden File Input */}
              <input
                id="hiddenImageInput"
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>

              <button
              className={`mt-4 w-full bg-[#3913C5] text-white px-4 py-2 rounded-lg flex items-center justify-center 
                ${!selectedFile ? "opacity-50 cursor-not-allowed" : "hover:bg-purple-700"}`}
              onClick={enhanceImage}
              disabled={!selectedFile || processing}
            >
              {processing ? (
                <>
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                  Enhancing... {progress}%
                </>
              ) : (
                "Enhance Image"
              )}
            </button>

              {error && <p className="mt-2 text-red-500 text-sm">{error}</p>}
              </div>
            </div>

            {/* Right Section - Enhanced Images */}
            <div className="w-full lg:w-1/2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-800">Enhanced Images</h2>
                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                  <button
                    onClick={() => setEnhancedViewMode("grid")}
                    className={`p-1.5 sm:p-2 rounded-md ${
                      enhancedViewMode === "grid" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setEnhancedViewMode("list")}
                    className={`p-1.5 sm:p-2 rounded-md ${
                      enhancedViewMode === "list" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>

              {enhancedViewMode === "grid" ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {paginatedEnhanced.map((img, index) => (
                    <div
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-[#3913C5]"
                      onMouseEnter={() => setHoveredImage(index)}
                      onMouseLeave={() => setHoveredImage(null)}
                    >
                        <img src={img} alt="Enhanced" className="w-full h-full object-contain p-1" />
                      {hoveredImage === index && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center gap-1 sm:gap-2">
                          <button
                            className="bg-white p-1.5 sm:p-2 rounded-full hover:bg-gray-200 transition-colors"
                            onClick={() => setImagePreview(img)}
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            className="bg-red-600 p-1.5 sm:p-2 rounded-full hover:bg-red-700 transition-colors"
                            onClick={() => handleDelete(index)}
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                          </button>
                          <button
                            className="bg-blue-600 p-1.5 sm:p-2 rounded-full hover:bg-blue-700 transition-colors"
                            onClick={() => handleDownload(img, index)}
                          >
                            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {paginatedEnhanced.map((img, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 sm:gap-3 p-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                      onMouseEnter={() => setHoveredImage(index)}
                      onMouseLeave={() => setHoveredImage(null)}
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded overflow-hidden">
                        <img src={img} alt="Enhanced" className="w-full h-full object-cover" />
                      </div>
                      <span className="text-xs sm:text-sm text-gray-600">Enhanced {index + 1}</span>
                      {hoveredImage === index && (
                        <div className="ml-auto flex items-center gap-1 sm:gap-2">
                          <button
                            className="p-1 sm:p-1.5 rounded-full hover:bg-gray-200 transition-colors"
                            onClick={() => setImagePreview(img)}
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            className="p-1 sm:p-1.5 rounded-full hover:bg-red-100 transition-colors"
                            onClick={() => handleDelete(index)}
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                          </button>
                          <button
                            className="p-1 sm:p-1.5 rounded-full hover:bg-blue-100 transition-colors"
                            onClick={() => handleDownload(img, index)}
                          >
                            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <EnhancedPagination />
              </div>
            </div>
          </div>

          {/* Bottom Section - Assets */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-800">Assets</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Choose an image from your saved assets</p>
                </div>
                <div className="flex items-center gap-2 mt-2 sm:mt-0">
                  <button
                    onClick={() => setAssetViewMode("grid")}
                    className={`p-1.5 sm:p-2 rounded-md ${
                      assetViewMode === "grid" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={() => setAssetViewMode("list")}
                    className={`p-1.5 sm:p-2 rounded-md ${
                      assetViewMode === "list" ? "bg-[#3913C5] text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>

              {assetViewMode === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                  {paginatedAssets.map((url, index) => (
                    <div
                      key={index}
                    className="relative aspect-square rounded-lg overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-[#3913C5] bg-gray-100"
                      onClick={() => selectAsset(url)}
                    >
                    <img src={url} alt="Asset" className="w-full h-full object-contain p-4" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {paginatedAssets.map((url, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 sm:gap-3 p-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
                      onClick={() => selectAsset(url)}
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded overflow-hidden">
                        <img src={url} alt="Asset" className="w-full h-full object-cover" />
                      </div>
                      <span className="text-xs sm:text-sm text-gray-600">Asset {index + 1}</span>
                    </div>
                  ))}
                </div>
              )}

              <AssetsPagination />
          </div>
        </div>

        {/* Image Preview Modal */}
        {imagePreview && (
          <div 
            className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-75 z-50"
            onClick={() => setImagePreview(null)}
          >
            <div className="relative max-w-4xl w-full mx-4">
              <button
                className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300 transition-colors"
                onClick={() => setImagePreview(null)}
              >
                <XCircle className="w-6 h-6" />
              </button>
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="max-w-full max-h-[80vh] rounded-lg shadow-xl object-contain"
              />
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteDialog && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Delete Image</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this image? This action cannot be undone.</p>
                <div className="flex gap-4 w-full">
                  <button
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setImageToDelete(null);
                    }}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default ImageEnhancer;